import { Navigate, Outlet } from 'react-router-dom';
import { Loader2, LayoutDashboard, LineChart, Building2, SlidersHorizontal, KeyRound, CreditCard } from 'lucide-react';
import { EnterpriseNav, type EnterpriseNavItem } from '@/components/enterprise/EnterpriseNav';
import { useEnterpriseCapabilities } from '@/hooks/useEnterpriseCapabilities';

/**
 * M4 (#569): the unified `/enterprise` shell. Gates on ANY enterprise
 * capability (cosmetic — child pages + server RPCs re-authorize), then renders
 * a clean monochrome underline section sub-nav (<EnterpriseNav>) above the
 * active child page.
 *
 * Nested INSIDE DashboardLayout's Outlet (a second-level Outlet), so the app
 * chrome + the cross-workspace deep-link consumer still mount.
 *
 * Tabs are CAPABILITY-GATED (hidden, not redirected): an admin sees all four;
 * a delegated monitor/analytics user sees only what their grants cover. Each
 * child page still self-gates (constraint 8) — this is cosmetic.
 */
export function EnterpriseLayout(): JSX.Element {
  const { isLoading, hasAnyEnterpriseCapability, partners } = useEnterpriseCapabilities();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!hasAnyEnterpriseCapability) {
    return <Navigate to="/" replace />;
  }

  // Any enterprise cap → Dashboard (adoption rollup is visible to monitors /
  // analytics viewers too). Effectively always true here, but kept explicit so
  // the gating intent reads consistently across tabs.
  const canSeeDashboard = partners.some(
    (p) =>
      p.is_admin ||
      p.capabilities.includes('email_monitor') ||
      p.capabilities.includes('analytics_read'),
  );
  const canSeeEmail = partners.some(
    (p) => p.is_admin || p.capabilities.includes('email_monitor'),
  );
  const isAnyAdmin = partners.some((p) => p.is_admin);
  // Members tab also hosts the Emulate launcher, which delegated `emulate`
  // grant-holders are authorized for (server RPCs re-authorize). Widen Members
  // visibility to them — SSO setup / Advanced stay admin-only below.
  const canEmulate = partners.some(
    (p) => p.is_admin || p.capabilities.includes('emulate'),
  );

  const items: EnterpriseNavItem[] = [
    canSeeDashboard && {
      icon: LayoutDashboard,
      label: 'Dashboard',
      to: '/enterprise/dashboard',
    },
    canSeeEmail && { icon: LineChart, label: 'Email', to: '/enterprise/email' },
    (isAnyAdmin || canEmulate) && { icon: Building2, label: 'Members', to: '/enterprise/members' },
    isAnyAdmin && { icon: KeyRound, label: 'SSO setup', to: '/enterprise/sso' },
    isAnyAdmin && {
      icon: SlidersHorizontal,
      label: 'Advanced',
      to: '/enterprise/advanced',
    },
    isAnyAdmin && { icon: CreditCard, label: 'Billing', to: '/enterprise/billing' },
  ].filter(Boolean) as EnterpriseNavItem[];

  return (
    <div className="flex h-full flex-col">
      <EnterpriseNav items={items} />
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
